/**
 * Store Pinia des réformes (composition API)
 */
import { defineStore } from "pinia";
import { ref } from "vue";
import { IReform, StateOfReform } from "../models/reform";
import { IReformApiResponse } from "../models/api/reformApiResponse";
import { IReformForm } from "../models/forms/reformForm";
import ReformServices from "../services/ReformService";
import { IReformFilter } from "../models/filters/reformFilter";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IAnimal } from "../models/animals";
import { useParameterStore } from "./parameter.store";
import { useBoars } from "./boars.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

/**
 * Store Pinia des réformes utilisant la Composition API
 */
const useReformStore = defineStore("reforms", () => {
    // State
    const datas = ref<IReform[]>([]);
    const current = ref<IReform>({} as IReform);
    const itemForm = ref<IReformForm>({} as IReformForm);

    // Actions

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     */
    async function load(filter: IReformFilter) {
        let result = await ReformServices.getAll<IReformFilter, IReformApiResponse>(filter);
        datas.value = [];

        if (!result.error) {
            datas.value = await hydrateReforms(result.data as IReformApiResponse[]);
        }

        return result;
    }

    /**
     * Hydrate une liste de réformes en batch (optimisé pour la performance)
     * - Charge les stores dépendants une seule fois en parallèle
     * - Crée des Maps d'index pour des lookups O(1)
     *
     * @param reforms Liste de réformes reçues de l'API avec des IDs
     * @returns Liste de réformes avec les références des stores
     */
    async function hydrateReforms(reforms: IReformApiResponse[]): Promise<IReform[]> {
        if (reforms.length === 0) return [];

        // 1. Récupérer les stores une seule fois
        const parameterStore = useParameterStore();
        const boarsStore = useBoars();

        // 2. Charger les stores dépendants en parallèle si nécessaire
        const loadPromises: Promise<unknown>[] = [];

        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }

        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Créer des Maps d'index pour des lookups O(1)
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));
        const boarsMap = new Map(boarsStore.datas.map(b => [b.id, b]));

        // 4. Hydrater toutes les réformes en une seule passe
        const models: IReform[] = [];

        for (const reform of reforms) {
            const model = { ...reform } as unknown as IReform;

            // Lier les références (lookup O(1) via Map)
            // Fallback sur l'objet API si l'animal n'est pas dans le store (ex: filtre centre)
            model.animal = boarsMap.get(reform.animalId) ?? model.animal;
            model.typeReform = parameterMap.get(reform.typeReformId) ?? model.typeReform;
            model.reason = parameterMap.get(reform.reasonId) ?? model.reason;

            // Conversion explicite des dates (les payloads SignalR arrivent en string JSON,
            // contrairement aux réponses Axios qui sont converties par le date transformer)
            if (reform.dateReform) model.dateReform = new Date(reform.dateReform as any);

            models.push(model);
        }

        return models;
    }

    /**
     * Récupération d'un reform par son identifiant
     * @param id
     */
    async function get(id: number) {
        let result = await ReformServices.getById<IReformApiResponse>(id);

        if (!result.error) {
            const [modelData] = await hydrateReforms([result.data as IReformApiResponse]);
            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0) {
                datas.value[index] = modelData;
            }
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    /**
     * Récupération de l'objet dans le store
     * @param id Identifiant de l'objet
     */
    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as IReform);
        return current.value;
    }

    /**
     * Sauvegarde de l'objet courant via le service
     * @param maj Mise à jour de la liste des données dans le store
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IReformApiResponse | IDataOperationResult<IReformApiResponse>>;

        if (itemForm.value.id > 0) {
            result = await ReformServices.update(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateReforms([result.data as IReformApiResponse]);
                current.value = modelData;
                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = "Item non trouvé dans le store";
                        return new ServiceOperationResult<IDataOperationResult<IReform>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await ReformServices.create(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateReforms([result.data as IReformApiResponse]);
                current.value = modelData;
                if (datas.value.findIndex((a) => a.id == current.value?.id) == -1) {
                    datas.value.push(current.value);
                }
            }
        }

        return result;
    }

    function toForm(reform: IReform): IReformForm {
        const form = {} as IReformForm;
        Object.assign(form, reform);
        form.animalId = reform.animal?.id;
        form.reasonId = reform.reason?.id ?? form.reasonId;
        form.typeReformId = reform.typeReform?.id ?? form.typeReformId;
        return form;
    }

    function newForm(lienId: number): IReformForm {
        itemForm.value = {
            id: 0,
            animalId: lienId,
            dateReform: new Date(),
            reasonId: 0,
            typeReformId: 0,
            observation: "",
            state: StateOfReform.StandBy
        } as IReformForm;
        return itemForm.value;
    }

    function newItem(lien: IAnimal): IReform {
        current.value = {
            animal: lien,
            dateReform: new Date(),
            reason: { id: 0, value: "" },
            typeReform: { id: 0, value: "" },
            state: StateOfReform.StandBy,
            observations: ""
        } as IReform;
        return current.value;
    }

    // Retourner les propriétés et méthodes publiques
    return {
        // State
        datas,
        current,
        itemForm,

        // Actions
        load,
        hydrateReforms,
        get,
        searchStoreById,
        save,
        toForm,
        newForm,
        newItem,
    };
});

signalRManager.connection.on("MajReform", async (payload: IPayloadStore) => {
    const store = useReformStore();
    const boarsStore = useBoars();

    switch (payload.action) {
        case "Create": {
            const [hydrated] = await store.hydrateReforms([payload.data as unknown as IReformApiResponse]);
            // Ajouter au reformStore si pas encore présent
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                store.datas.push(hydrated);
            }
            // Toujours synchroniser le boar inliné
            const boarIdxCreate = boarsStore.datas.findIndex(b => b.id === hydrated.animalId);
            if (boarIdxCreate > -1) {
                boarsStore.datas.splice(boarIdxCreate, 1, { ...boarsStore.datas[boarIdxCreate], reform: hydrated });
            }
            break;
        }
        case "Update": {
            const [hydrated] = await store.hydrateReforms([payload.data as unknown as IReformApiResponse]);
            // Mettre à jour le reformStore si la réforme y est présente
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                store.datas[idx] = hydrated;
            }
            // Toujours synchroniser le boar inliné (même si la réforme n'est pas dans reformStore.datas,
            // ex: autre utilisateur qui n'a pas ouvert la modale de réforme)
            const boarIdx = boarsStore.datas.findIndex(b => b.id === hydrated.animalId);
            if (boarIdx > -1) {
                boarsStore.datas.splice(boarIdx, 1, { ...boarsStore.datas[boarIdx], reform: hydrated });
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) {
                store.datas.splice(idx, 1);
            }
            // Toujours synchroniser le boar inliné (même si la réforme n'est pas dans reformStore.datas)
            const boarIdxDelete = boarsStore.datas.findIndex(b => b.reform?.id === payload.id);
            if (boarIdxDelete > -1) {
                const boarCopy = { ...boarsStore.datas[boarIdxDelete] };
                delete (boarCopy as any).reform;
                boarsStore.datas.splice(boarIdxDelete, 1, boarCopy);
            }
            break;
        }
    }
});

export { useReformStore };
